/**
 * Player settings: how the table is shown, and volume (UI-004).
 *
 * Stored on the device in `localStorage` (docs/access-patterns.md §4, "Player
 * settings"). Everything read back is UNTRUSTED (PRD §5): it is validated field by
 * field, and anything invalid is discarded in favour of the default rather than
 * clamped into range — a value we did not write is not a value we should interpret.
 *
 * Nothing here affects a shot or a score. These are display and volume preferences,
 * so a player editing them by hand can only change what they themselves see and hear.
 */

import {
  DEFAULT_THEME,
  OFFERED_THEMES,
  isOfferedTheme,
  type ThemeId,
  type ThemeInfo,
} from '../config/themes.ts';
import { RELEASES } from '../config/releases.ts';
import {
  DEFAULT_CUE,
  isOfferedCue,
  offeredCues,
  type CueInfo,
  type CueStyleId,
} from '../config/cues.ts';

export type TableOrientation =
  /** Long axis across the screen, with the controls below the table (UI-006). */
  | 'landscape'
  /** Long axis up the screen. */
  | 'portrait';

export interface Settings {
  readonly orientation: TableOrientation;
  /** Fraction of the available space the table fills. 1 is as large as fits. */
  readonly tableScale: number;
  /** 0–1. There is no music yet; the setting is kept for when there is. */
  readonly musicVolume: number;
  /** 0–1. There are no sound effects yet; likewise. */
  readonly effectsVolume: number;
  /** Table and page theme (THM). Validated against the list in config/themes.ts. */
  readonly theme: ThemeId;
  /** Draw the pool cue behind the white ball while aiming (UI-015). */
  readonly showCue: boolean;
  /** The pool cue's length, as a fraction of its longest (UI-018). */
  readonly cueLength: number;
  /** The pool cue's look (UI-019). */
  readonly cueStyle: CueStyleId;
}

/**
 * The themes and cue styles a player may pick (D-047, UI-019): the free ones, all of
 * them once the supporter pack is released, and all of them in debug mode on the local
 * development server. A saved choice that is not offered falls back to the default.
 */
export interface Offer {
  readonly themes: readonly ThemeInfo[];
  readonly cues: readonly CueInfo[];
}

export const DEFAULT_OFFER: Offer = {
  themes: OFFERED_THEMES,
  cues: offeredCues(RELEASES.supporterPack),
};

export const CUE_LENGTH_MIN = 0.25;
export const CUE_LENGTH_MAX = 1;

export const TABLE_SCALE_MIN = 0.6;
export const TABLE_SCALE_MAX = 1;

export const DEFAULT_SETTINGS: Settings = {
  // Only a fallback. A first visit is given whichever fits the screen (UI-006, main.ts).
  orientation: 'landscape',
  tableScale: 1,
  musicVolume: 0.6,
  effectsVolume: 0.8,
  theme: DEFAULT_THEME,
  showCue: true,
  // Owner, 2026-09-27 (UI-019): 90% of the longest.
  cueLength: 0.9,
  cueStyle: DEFAULT_CUE,
};

/** Versioned, so a future change of shape can ignore old data instead of misreading it. */
export const SETTINGS_KEY = 'potted.settings.v1';

/*
 * UI-006: 'auto' was removed at the owner's request. A stored 'auto' is now invalid,
 * so it falls back to the screen-fitted default, exactly like a first visit.
 */
const ORIENTATIONS: readonly TableOrientation[] = ['landscape', 'portrait'];

function inRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function isOrientation(value: unknown): value is TableOrientation {
  return typeof value === 'string' && (ORIENTATIONS as readonly string[]).includes(value);
}

/**
 * Validate anything at all into a complete `Settings`. Never throws. Anything missing
 * or invalid takes its value from `defaults`.
 */
export function parseSettings(
  raw: unknown,
  defaults: Settings = DEFAULT_SETTINGS,
  offer: Offer = DEFAULT_OFFER,
): Settings {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return defaults;
  const r = raw as Record<string, unknown>;
  return {
    orientation: isOrientation(r['orientation']) ? r['orientation'] : defaults.orientation,
    tableScale: inRange(r['tableScale'], TABLE_SCALE_MIN, TABLE_SCALE_MAX)
      ? r['tableScale']
      : defaults.tableScale,
    musicVolume: inRange(r['musicVolume'], 0, 1) ? r['musicVolume'] : defaults.musicVolume,
    effectsVolume: inRange(r['effectsVolume'], 0, 1) ? r['effectsVolume'] : defaults.effectsVolume,
    // Only an offered theme (D-047): one saved before the pack was withdrawn falls back.
    theme: isOfferedTheme(r['theme'], offer.themes) ? r['theme'] : defaults.theme,
    showCue: typeof r['showCue'] === 'boolean' ? r['showCue'] : defaults.showCue,
    cueLength: inRange(r['cueLength'], CUE_LENGTH_MIN, CUE_LENGTH_MAX)
      ? r['cueLength']
      : defaults.cueLength,
    cueStyle: isOfferedCue(r['cueStyle'], offer.cues) ? r['cueStyle'] : defaults.cueStyle,
  };
}

/** The slice of the Web Storage API this needs — injectable, so tests need no DOM. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  /** Optional: only forgetting needs it (THM-004), and test stores may leave it out. */
  removeItem?(key: string): void;
}

export interface SettingsStore {
  load(): Settings;
  save(settings: Settings): void;
}

/**
 * Storage can be missing, full, or throw on access (private windows, blocked site
 * data). Settings are a convenience: when storage fails, the game carries on with the
 * defaults rather than breaking.
 *
 * `defaults` are what a first visit gets, and what any unreadable field falls back to.
 * The caller fits the orientation to the screen (UI-006).
 */
export function createSettingsStore(
  storage: KeyValueStore | undefined,
  defaults: Settings = DEFAULT_SETTINGS,
  offer: Offer = DEFAULT_OFFER,
): SettingsStore {
  return {
    load() {
      if (storage === undefined) return defaults;
      try {
        const text = storage.getItem(SETTINGS_KEY);
        return text === null ? defaults : parseSettings(JSON.parse(text), defaults, offer);
      } catch {
        return defaults;
      }
    },
    save(settings) {
      if (storage === undefined) return;
      try {
        storage.setItem(SETTINGS_KEY, JSON.stringify(parseSettings(settings, defaults, offer)));
      } catch {
        // Not saved; the settings still apply for this visit.
      }
    },
  };
}
